/*
  Higher Order Component (HOC) to provide a list of recommendations to your
  component.

  Usage:
  const ComponentWithRecommendations = withRecommendations()(Component);
*/

import React, { Component } from "react";
import { connect } from "react-redux";
import { productPropType, userDataPropType } from "components/propTypes";

function getRecommendations(product, userData) {
  const { fitRates } = product || {};

  if (!fitRates || !Array.isArray(fitRates)) return null;

  const { userProfile } = userData || {};

  if (!userProfile) return null; // anonymous

  const { profiles: { primarySize } = {} } = userProfile;

  const recommendations = fitRates
    .filter(({ userSize }) => userSize === primarySize)
    .map(({ size, canonicalSize, fitRate, numResponses, isRecommended, userSize, priority }) => ({
      size,
      percentage: Math.round(fitRate * 100),
      surveyCount: numResponses,
      isRecommended,
      canonicalSize,
      userSize,
      priority,
    }))
    .sort(({ canonicalSize: a }, { canonicalSize: b }) => parseInt(a, 10) - parseInt(b, 10));

  // group the recommendations by canonical size
  const canonicalSizeGroups = recommendations.reduce((memo, recommendation) => {
    const key = parseInt(recommendation.canonicalSize, 10);
    (memo[key] = memo[key] || []).push(recommendation);
    return memo;
  }, {});

  // sort each group by size
  return Object.values(canonicalSizeGroups).flatMap(groups =>
    groups.sort(({ size: a }, { size: b }) => parseInt(a, 10) - parseInt(b, 10))
  );
}

export function withRecommendations() {
  return WrappedComponent => {
    class ComponentWithUserData extends Component {
      static propTypes = {
        product: productPropType,
        userData: userDataPropType.isRequired, // provided by connect below
      };

      render() {
        const { product, userData } = this.props;

        const recommendations = getRecommendations(product, userData);

        // eslint-disable-next-line react/jsx-props-no-spreading
        return <WrappedComponent {...this.props} recommendations={recommendations} />;
      }
    }

    const mapStateToProps = ({ userData = {} }) => ({ userData });

    const C = connect(mapStateToProps, null)(ComponentWithUserData);
    const componentName = WrappedComponent.displayName || WrappedComponent.name;
    C.displayName = `withRecommendations(${componentName})`;

    return C;
  };
}
